import { createTamagui } from 'tamagui'
import { defaultConfig } from '@tamagui/config/v4'

const spaceGroteskFont = {
  family: 'Space Grotesk',
  face: {
    300: { normal: 'Space Grotesk' },
    400: { normal: 'Space Grotesk' },
    500: { normal: 'Space Grotesk' },
    600: { normal: 'Space Grotesk' },
    700: { normal: 'Space Grotesk' },
    800: { normal: 'Space Grotesk' },
    900: { normal: 'Space Grotesk' },
  },
  size: {
    1: 11, 2: 12, 3: 13, 4: 14, 5: 16, 6: 18, 7: 20, 8: 24, 9: 32,
    10: 48, 11: 56, 12: 64, 13: 80, 14: 96, 15: 128, true: 16,
  },
  lineHeight: {
    1: 14, 2: 16, 3: 18, 4: 20, 5: 22, 6: 26, 7: 28, 8: 32, 9: 40,
    10: 56, 11: 64, 12: 72, 13: 88, 14: 104, 15: 140, true: 22,
  },
  weight: { 1: '300', 2: '400', 3: '500', 4: '600', 5: '700', 6: '800', 7: '900' },
  letterSpacing: {
    1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: -0.5, 7: -0.5, 8: -0.5,
    9: -1, 10: -1.5, 11: -2, 12: -2, 13: -2.5, 14: -3, 15: -3, true: 0,
  },
}

// Modern dark — warm stone base with Roman red accent.
//
// The ground sits at #17120f rather than near-black: at #0b0909 the surfaces
// collapsed into one another, so cards, rules and hovers had nowhere to sit and
// the page read as a void with text on it. Lifting the base and widening the
// gaps between color1–5 gives the layout actual depth. To go lighter or darker,
// move this whole ramp together — changing `background` alone flattens it again.
const modernDark = {
  // Deep ground, warm-tinted. The earlier flatness was never the floor being low
  // — it was color1-4 sitting within ~30 values of each other, so borders and
  // hovers had nothing to sit against. The fix is spreading the ramp, not
  // raising it. Keep this deep and widen the gaps instead.
  background: '#0b0909',
  backgroundHover: '#151111',
  backgroundPress: '#080707',
  backgroundFocus: '#151111',
  backgroundStrong: '#060505',
  backgroundTransparent: 'rgba(11, 9, 9, 0)',

  // Text — pure neutral, no tint
  color: '#f2ede7',
  colorHover: '#ffffff',
  colorPress: '#d4d4d8',
  colorFocus: '#ffffff',
  colorTransparent: 'rgba(250, 250, 250, 0)',

  // color1-5: warm-red tinted surfaces / color6-12: pure neutral text
  color1: '#0b0909',
  color2: '#141010',
  color3: '#1f1919',
  color4: '#2e2626',
  color5: '#443a3a',
  color6: '#6b5f5b',
  color7: '#8f8179',
  color8: '#b5a89e',
  color9: '#d4d4d8',
  color10: '#e4e4e7',
  color11: '#ece5dd',
  color12: '#f5f0ea',

  accentBackground: '#a03030',
  accentColor: '#fafafa',

  borderColor: '#1f1919',
  borderColorHover: '#2e2626',
  borderColorPress: '#1f1919',
  borderColorFocus: '#a03030',

  shadowColor: 'rgba(0, 0, 0, 0.5)',
  shadowColorHover: 'rgba(0, 0, 0, 0.6)',

  // Roman red accent
  amber1: '#140808', amber2: '#1e0e0e', amber3: '#2e1414', amber4: '#401c1c',
  amber5: '#582424', amber6: '#702c2c', amber7: '#a03030', amber8: '#b84040',
  amber9: '#a03030', amber10: '#b84040', amber11: '#d06060', amber12: '#e8a0a0',

  green1: '#0a1210', green2: '#0f1e1a', green3: '#142e26', green4: '#1a3e32',
  green5: '#204e3e', green6: '#285e4c', green7: '#34d399', green8: '#6ee7b7',
  green9: '#34d399', green10: '#6ee7b7', green11: '#a7f3d0', green12: '#d1fae5',

  blue1: '#0a1020', blue2: '#0f182e', blue3: '#142440', blue4: '#1a3058',
  blue5: '#203c6e', blue6: '#284a85', blue7: '#60a5fa', blue8: '#93c5fd',
  blue9: '#60a5fa', blue10: '#93c5fd', blue11: '#bfdbfe', blue12: '#dbeafe',

  purple1: '#100a20', purple2: '#180f2e', purple3: '#221440', purple4: '#2e1a58',
  purple5: '#3a206e', purple6: '#482885', purple7: '#a78bfa', purple8: '#c4b5fd',
  purple9: '#a78bfa', purple10: '#c4b5fd', purple11: '#ddd6fe', purple12: '#ede9fe',

  red1: '#140a0a', red2: '#1e0f0f', red3: '#2e1414', red4: '#401a1a',
  red5: '#582020', red6: '#702828', red7: '#f87171', red8: '#fca5a5',
  red9: '#f87171', red10: '#fca5a5', red11: '#fecaca', red12: '#fee2e2',

  yellow1: '#14120a', yellow2: '#1e1a0f', yellow3: '#2e2614', yellow4: '#40341a',
  yellow5: '#584420', yellow6: '#705628', yellow7: '#fbbf24', yellow8: '#fcd34d',
  yellow9: '#fbbf24', yellow10: '#fcd34d', yellow11: '#fde68a', yellow12: '#fef9c3',
}

// Modern light — parchment and oxblood.
//
// Deliberately not a cool zinc grey: carved limestone with red ochre rubbed into
// the letters is what a Roman inscription actually looked like, so warm ground
// and warm ink carry the identity instead of fighting it. Ink is #1c1815 rather
// than a true black for the same reason.
const modernLight = {
  background: '#f2ede3',
  backgroundHover: '#e9e2d4',
  backgroundPress: '#ddd4c2',
  backgroundFocus: '#e9e2d4',
  backgroundStrong: '#e9e2d4',
  backgroundTransparent: 'rgba(242, 237, 227, 0)',

  color: '#241f1b',
  colorHover: '#1c1815',
  colorPress: '#322c27',
  colorFocus: '#1c1815',
  colorTransparent: 'rgba(36, 31, 27, 0)',

  color1: '#f2ede3',
  color2: '#e9e2d4',
  color3: '#ddd4c2',
  color4: '#cfc4ae',
  color5: '#bcae95',
  color6: '#9c8e77',
  color7: '#7d7161',
  color8: '#6b6055',
  color9: '#4d443c',
  color10: '#322c27',
  color11: '#241f1b',
  color12: '#1c1815',

  accentBackground: '#8b2828',
  accentColor: '#ffffff',

  borderColor: '#e4e4e7',
  borderColorHover: '#d4d4d8',
  borderColorPress: '#e4e4e7',
  borderColorFocus: '#8b2828',

  shadowColor: 'rgba(0, 0, 0, 0.06)',
  shadowColorHover: 'rgba(0, 0, 0, 0.1)',

  amber1: '#fefcf4', amber2: '#fdf8e4', amber3: '#faf0c8', amber4: '#f4e4a0',
  amber5: '#e8d078', amber6: '#d4b850', amber7: '#b8962a', amber8: '#9a7c20',
  amber9: '#b8962a', amber10: '#9a7c20', amber11: '#7c6418', amber12: '#5e4c10',

  green1: '#f0fdf4', green2: '#dcfce7', green3: '#bbf7d0', green4: '#86efac',
  green5: '#4ade80', green6: '#22c55e', green7: '#16a34a', green8: '#15803d',
  green9: '#16a34a', green10: '#15803d', green11: '#166534', green12: '#14532d',

  blue1: '#eff6ff', blue2: '#dbeafe', blue3: '#bfdbfe', blue4: '#93c5fd',
  blue5: '#60a5fa', blue6: '#3b82f6', blue7: '#2563eb', blue8: '#1d4ed8',
  blue9: '#2563eb', blue10: '#1d4ed8', blue11: '#1e40af', blue12: '#1e3a8a',

  purple1: '#faf5ff', purple2: '#f3e8ff', purple3: '#e9d5ff', purple4: '#d8b4fe',
  purple5: '#c084fc', purple6: '#a855f7', purple7: '#9333ea', purple8: '#7e22ce',
  purple9: '#9333ea', purple10: '#7e22ce', purple11: '#6b21a8', purple12: '#581c87',

  red1: '#fef2f2', red2: '#fee2e2', red3: '#fecaca', red4: '#fca5a5',
  red5: '#f87171', red6: '#ef4444', red7: '#dc2626', red8: '#b91c1c',
  red9: '#dc2626', red10: '#b91c1c', red11: '#991b1b', red12: '#7f1d1d',

  yellow1: '#fefce8', yellow2: '#fef9c3', yellow3: '#fef08a', yellow4: '#fde047',
  yellow5: '#facc15', yellow6: '#eab308', yellow7: '#ca8a04', yellow8: '#a16207',
  yellow9: '#eab308', yellow10: '#ca8a04', yellow11: '#a16207', yellow12: '#854d0e',
}

const radius = {
  0: 0, 1: 2, 2: 4, 3: 6, 4: 8, 5: 10, 6: 12, 7: 14, 8: 16,
  9: 20, 10: 24, 11: 28, 12: 32, true: 8,
}

export const config = createTamagui({
  ...defaultConfig,
  themes: {
    ...defaultConfig.themes,
    dark: modernDark,
    light: modernLight,
  },
  fonts: {
    ...defaultConfig.fonts,
    body: spaceGroteskFont,
    heading: spaceGroteskFont,
  },
  radius,
})

export default config
export type AppConfig = typeof config
declare module 'tamagui' {
  interface TamaguiCustomConfig extends AppConfig {}
}
